'use client';

import { useLayoutDispatch, useLayoutState } from 'BomdisoftComponents/layout/context';
import { type DrawerConfig } from 'BomdisoftComponents/layout/types';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

export const useDrawer = (drawerId: string) => {
  const dispatch = useLayoutDispatch();
  const drawerState = useLayoutState(state => state.drawers[drawerId]) as DrawerConfig | undefined;

  return {
    close: () => dispatch({ id: drawerId, type: 'CLOSE_DRAWER' }),
    isOpen: drawerState?.open ?? false,
    open: () => dispatch({ id: drawerId, type: 'OPEN_DRAWER' }),
    resize: (width: number) => dispatch({ id: drawerId, type: 'RESIZE_DRAWER', width }),
    toggle: () => dispatch({ id: drawerId, type: 'TOGGLE_DRAWER' }),
    width: drawerState?.width ?? 0,
  };
};

export const useRegisterDrawer = (id: string, { maxWidth, minWidth, width }: { maxWidth: number; minWidth: number; width: number }) => {
  const dispatch = useLayoutDispatch();

  useEffect(() => {
    dispatch({ config: { maxWidth, minWidth, width }, id, type: 'REGISTER_DRAWER' });
    return () => {
      dispatch({ id, type: 'UNREGISTER_DRAWER' });
    };
  }, [dispatch, id, maxWidth, minWidth, width]);
};

export const useCloseDrawersOnNavigation = () => {
  const dispatch = useLayoutDispatch();
  const drawers = useLayoutState(state => state.drawers);
  const pathname = usePathname();
  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      for (const id of Object.keys(drawers)) {
        if (drawers[id]?.open) {
          dispatch({ id, type: 'CLOSE_DRAWER' });
        }
      }
    }
  }, [dispatch, drawers, pathname]);
};

export const useLayoutSidebar = () => {
  const dispatch = useLayoutDispatch();
  const sidebarOpen = useLayoutState(state => state.sidebarOpen);

  return {
    isOpen: sidebarOpen,
    setSidebar: (open: boolean) => dispatch({ open, type: 'SET_SIDEBAR' }),
    toggle: () => dispatch({ type: 'TOGGLE_SIDEBAR' }),
  };
};
